import { useRef, useEffect, useMemo } from 'react';
import * as THREE from 'three';

// Simple Line component
export function Line({
    points,
    color = 'white',
    lineWidth = 5
}: {
    points: THREE.Vector3[];
    color?: string;
    lineWidth?: number;
}) {
    const ref = useRef<THREE.Line>(null);

    useEffect(() => {
        if (ref.current) {
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            ref.current.geometry = geometry;
        }
    }, [points]);

    return (
        <line ref={ref as any}>
            <bufferGeometry />
            <lineBasicMaterial color={color} linewidth={lineWidth} opacity={1} transparent={false} />
        </line>
    );
}

// Dashed Line component for axes
export function DashedLine({
    points,
    color = 'white',
    dashSize = 0.1,
    gapSize = 0.05,
    lineWidth = 5,
    opacity = 1
}: {
    points: THREE.Vector3[];
    color?: string;
    dashSize?: number;
    gapSize?: number;
    lineWidth?: number;
    opacity?: number;
}) {
    const ref = useRef<THREE.Line>(null);

    useEffect(() => {
        if (ref.current) {
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            ref.current.geometry = geometry;
            // Required for dashed lines to render properly
            // @ts-ignore
            if (typeof ref.current.computeLineDistances === 'function') {
                // @ts-ignore
                ref.current.computeLineDistances();
            }
        }
    }, [points]);

    return (
        <line ref={ref as any}>
            <bufferGeometry />
            <lineDashedMaterial color={color} linewidth={lineWidth} dashSize={dashSize} gapSize={gapSize} transparent={opacity < 1} opacity={opacity} />
        </line>
    );
}

// Text Label component - just renders a sphere marker
export function Label({
    position,
    color = 'white'
}: {
    position: [number, number, number];
    color?: string;
}) {
    return (
        <mesh position={position}>
            <sphereGeometry args={[0.06]} />
            <meshBasicMaterial color={color} />
        </mesh>
    );
}

// Thick Line component using flattened boxes to simulate webbing straps (kayış)
export function ThickLine({
    points,
    color = 'white',
    width = 0.06,      // Wide side of the strap
    thickness = 0.005  // Thin side of the strap
}: {
    points: THREE.Vector3[];
    color?: string;
    width?: number;
    thickness?: number;
}) {
    const segments = useMemo(() => {
        const segs: { position: THREE.Vector3; quaternion: THREE.Quaternion; length: number }[] = [];
        for (let i = 0; i < points.length - 1; i++) {
            const start = points[i];
            const end = points[i + 1];
            const direction = new THREE.Vector3().subVectors(end, start);
            const length = direction.length();

            if (length === 0) continue;

            const position = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);

            // Standard orientation: Y is up (length), X is wide, Z is thin
            const quaternion = new THREE.Quaternion().setFromUnitVectors(
                new THREE.Vector3(0, 1, 0),
                direction.clone().normalize()
            );

            segs.push({ position, quaternion, length });
        }
        return segs;
    }, [points]);

    return (
        <group>
            {segments.map((seg, idx) => (
                <mesh key={idx} position={seg.position} quaternion={seg.quaternion}>
                    <boxGeometry args={[width, seg.length, thickness]} />
                    <meshStandardMaterial color={color} metalness={0.1} roughness={0.9} />
                </mesh>
            ))}
        </group>
    );
}

// Simple axis text label rendered as a Sprite
export function AxisLabel({
    position,
    text,
    color = '#ffffff',
    scale = 0.8,
    segments,
    depthTest = true,
}: {
    position: [number, number, number];
    text?: string;
    color?: string;
    scale?: number;
    segments?: { text: string; color: string }[];
    depthTest?: boolean;
}) {
    // Determine the text lines to render either from segments or by splitting the text string
    const parts = useMemo(() => {
        if (segments && segments.length > 0) return segments;
        if (text) return text.split(' + ').map(p => ({ text: p, color }));
        return [];
    }, [text, color, segments]);
 
    const lineCount = Math.max(1, parts.length);
    const texture = useMemo(() => {
        const canvas = document.createElement('canvas');
        const scaleFactor = 3; // Even higher supersampling for maximum sharpness
        const width = 1280 * scaleFactor;
        const baseLineHeight = 200 * scaleFactor;
        const height = baseLineHeight * lineCount + (80 * scaleFactor);
 
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
 
        ctx.clearRect(0, 0, width, height);
 
        // Draw Text - High resolution for maximum sharpness
        const baseFontSize = 140 * scaleFactor; // Increased font size relative to line height
        ctx.font = `500 ${baseFontSize}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`; // Changed weight from 700 to 500
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
 
        // Measure maximum width of parts
        let maxWidthMeasure = 0;
        parts.forEach(p => {
            const m = ctx.measureText(p.text + (parts.length > 1 ? ' +' : '')).width;
            if (m > maxWidthMeasure) maxWidthMeasure = m;
        });
 
        // If text is wider than ~90% of canvas, shrink font
        const canvasPadding = 120 * scaleFactor;
        if (maxWidthMeasure > (width - canvasPadding)) {
            const ratio = (width - canvasPadding) / maxWidthMeasure;
            ctx.font = `500 ${baseFontSize * ratio}px system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;
        }
 
        if (parts.length > 1) {
            parts.forEach((part, index) => {
                const yPos = (index + 0.5) * baseLineHeight + (15 * scaleFactor);
                ctx.fillStyle = part.color;
                ctx.fillText(part.text + (index < parts.length - 1 ? ' +' : ''), width / 2, yPos);
            });
        } else if (parts.length === 1) {
            ctx.fillStyle = parts[0].color;
            ctx.fillText(parts[0].text, width / 2, height / 2);
        }
 
 
        const texture = new THREE.CanvasTexture(canvas);
        texture.anisotropy = 16;
        texture.needsUpdate = true;
        return texture;
    }, [parts, lineCount]);
 
    if (!texture) return null;
 
    const baseSpriteHeight = 0.42; // Increased from 0.35
    const spriteYScale = scale * baseSpriteHeight * (lineCount + 0.1);
 
    return (
        <sprite position={position} scale={[scale * 4.2, spriteYScale, 1]} renderOrder={depthTest ? 0 : 100}>
            <spriteMaterial map={texture} transparent depthTest={depthTest} depthWrite={false} />
        </sprite>
    );
}
